import React, { createContext, useContext, ReactNode } from "react";

/**
 * Context data structure for sharing flyer viewer state across coordinate box components.
 *
 * This interface defines the shared context that eliminates props drilling by providing
 * essential image dimensions, scale factors, and positioning information to all child
 * components. The context enables coordinate boxes to properly position and scale themselves
 * relative to the displayed flyer image.
 *
 * @property scaleX - Scale factor for X-axis (displayed width / natural width)
 * @property scaleY - Scale factor for Y-axis (displayed height / natural height)
 * @property imageBounds - Natural dimensions of the flyer image in original pixels
 * @property imageOffset - X/Y offset values for centering the image within the container
 * @property multiSelectEnabled - When true, multiple coordinate boxes can be selected; when false, only one at a time
 */
export interface FlyerCoordinateEditorContextData {
  /** Scale factor for X-axis (image displayed width / natural width) */
  scaleX: number;
  /** Scale factor for Y-axis (image displayed height / natural height) */
  scaleY: number;
  /** Natural dimensions of the flyer image (in original pixels) */
  imageBounds: {
    width: number;
    height: number;
  };
  /** Offset for centering the image in the container */
  imageOffset: {
    x: number;
    y: number;
  };
  /** When true, multiple boxes can be selected; when false, only one box at a time */
  multiSelectEnabled: boolean;
}

const FlyerCoordinateEditorContext = createContext<
  FlyerCoordinateEditorContextData | undefined
>(undefined);

export interface FlyerCoordinateEditorContextProviderProps {
  value: FlyerCoordinateEditorContextData;
  children: ReactNode;
}

/**
 * Provider component that supplies FlyerCoordinateEditorContext to its descendants.
 *
 * This component wraps the canvas/stage area where coordinate boxes are rendered,
 * making context data available to all child CoordinateBox components without
 * the need for prop drilling.
 *
 * @param props - Component properties
 * @param props.value - The context data to provide to descendants
 * @param props.children - Child components that will have access to the context
 */
export const FlyerCoordinateEditorContextProvider: React.FC<
  FlyerCoordinateEditorContextProviderProps
> = ({ value, children }) => {
  return (
    <FlyerCoordinateEditorContext.Provider value={value}>
      {children}
    </FlyerCoordinateEditorContext.Provider>
  );
};

/**
 * Custom hook for accessing FlyerCoordinateEditor context data.
 *
 * Retrieves the context data containing image dimensions, scale factors, and positioning
 * information. This hook should only be used within components that are descendants of
 * FlyerCoordinateEditorContextProvider. The hook automatically validates that the context exists
 * and throws a descriptive error if used incorrectly.
 *
 * @throws {Error} Throws an error if the hook is used outside of FlyerCoordinateEditorContextProvider
 * @returns Context data object containing scaleX, scaleY, imageBounds, and imageOffset
 * @example
 * ```tsx
 * const { scaleX, scaleY, imageBounds, imageOffset } = useFlyerCoordinateEditorContext();
 * ```
 */
export const useFlyerCoordinateEditorContext =
  (): FlyerCoordinateEditorContextData => {
    const context = useContext(FlyerCoordinateEditorContext);
    if (!context) {
      throw new Error(
        "useFlyerCoordinateEditorContext must be used within FlyerCoordinateEditorContextProvider",
      );
    }
    return context;
  };
